import React, { useState, useEffect, useRef } from 'react';
import { Code2, Globe, Terminal, ChevronDown, ChevronUp, FolderTree } from 'lucide-react';
import AgentFileTree from './AgentFileTree';
import AgentEditor from './AgentEditor';
import AgentLiveView from './AgentLiveView';
import AgentTerminal from './AgentTerminal';

const TERMINAL_OPEN_KEY = 'odium_ai_terminal_open_v1';
const TERMINAL_HEIGHT_KEY = 'odium_ai_terminal_height_pct_v1';
const TERMINAL_HEIGHT_MIN = 10;
const TERMINAL_HEIGHT_MAX = 90;
const TERMINAL_HEIGHT_DEFAULT = 38;

const clampTerminalHeight = (pct) =>
  Math.min(TERMINAL_HEIGHT_MAX, Math.max(TERMINAL_HEIGHT_MIN, Number(pct) || TERMINAL_HEIGHT_DEFAULT));

/**
 * Cursor-ähnliche Spalte: Dateien | Editor/Live oben, Terminal unten.
 * mobilePanel: null = Desktop (beides), 'files' | 'editor' = nur dieser Teil.
 */
const AgentWorkspace = ({
  projectId,
  onProjectIdChange,
  activeFilePath,
  onActiveFilePathChange,
  agentWritingPath = '',
  onFollowAgentFile,
  pinnedPaths = [],
  onPinFile,
  treeRefresh = 0,
  previewRefresh = 0,
  mobilePanel = null,
}) => {
  const [mainTab, setMainTab] = useState('editor');
  const [createHint, setCreateHint] = useState(false);
  const [localTreeTick, setLocalTreeTick] = useState(0);
  const [localPreviewTick, setLocalPreviewTick] = useState(0);
  const [localTerminalTick, setLocalTerminalTick] = useState(0);
  const [terminalOpen, setTerminalOpen] = useState(() => {
    try {
      const stored = localStorage.getItem(TERMINAL_OPEN_KEY);
      if (stored === '0') return false;
      if (stored === '1') return true;
      return true;
    } catch (_) {
      return true;
    }
  });
  const [terminalHeightPct, setTerminalHeightPct] = useState(() => {
    try {
      const stored = parseFloat(localStorage.getItem(TERMINAL_HEIGHT_KEY) || '');
      if (Number.isFinite(stored)) return clampTerminalHeight(stored);
    } catch (_) {
      /* ignore */
    }
    return TERMINAL_HEIGHT_DEFAULT;
  });
  const splitRef = useRef(null);
  const resizingTerminalRef = useRef(false);
  const terminalHeightRef = useRef(terminalHeightPct);
  const resizeStartRef = useRef({ y: 0, pct: TERMINAL_HEIGHT_DEFAULT });

  useEffect(() => {
    terminalHeightRef.current = terminalHeightPct;
  }, [terminalHeightPct]);

  useEffect(() => {
    const onMove = (e) => {
      if (!resizingTerminalRef.current || !splitRef.current) return;
      const h = splitRef.current.getBoundingClientRect().height;
      if (h <= 0) return;
      const deltaY = resizeStartRef.current.y - e.clientY;
      const next = clampTerminalHeight(resizeStartRef.current.pct + (deltaY / h) * 100);
      terminalHeightRef.current = next;
      setTerminalHeightPct(next);
    };
    const onUp = () => {
      if (!resizingTerminalRef.current) return;
      resizingTerminalRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      try {
        localStorage.setItem(TERMINAL_HEIGHT_KEY, String(Math.round(terminalHeightRef.current * 10) / 10));
      } catch (_) {
        /* ignore */
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const showFiles = !mobilePanel || mobilePanel === 'files';
  const showEditor = !mobilePanel || mobilePanel === 'editor';
  const isMobileSlice = mobilePanel === 'files' || mobilePanel === 'editor';
  const writingElsewhere = (() => {
    const writing = String(agentWritingPath || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .trim();
    const open = String(activeFilePath || '')
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .trim();
    return Boolean(writing && writing !== open);
  })();
  const writingName = String(agentWritingPath || '')
    .replace(/\\/g, '/')
    .split('/')
    .filter(Boolean)
    .pop() || agentWritingPath;

  useEffect(() => {
    setLocalTreeTick((n) => n + 1);
  }, [treeRefresh]);

  useEffect(() => {
    setLocalPreviewTick((n) => n + 1);
    setLocalTerminalTick((n) => n + 1);
  }, [previewRefresh]);

  useEffect(() => {
    const onFocus = (ev) => {
      const d = ev?.detail || {};
      if (d.tab === 'live' || d.tab === 'editor') setMainTab(d.tab);
      if (d.terminal === true) {
        setTerminalOpen(true);
        try {
          localStorage.setItem(TERMINAL_OPEN_KEY, '1');
        } catch (_) {
          /* ignore */
        }
      }
      if (d.refreshPreview) setLocalPreviewTick((n) => n + 1);
      if (d.refreshTerminal || d.terminal) setLocalTerminalTick((n) => n + 1);
    };
    window.addEventListener('odium:agent-workspace', onFocus);
    return () => window.removeEventListener('odium:agent-workspace', onFocus);
  }, []);

  const toggleTerminal = () => {
    setTerminalOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(TERMINAL_OPEN_KEY, next ? '1' : '0');
      } catch (_) {
        /* ignore */
      }
      if (next) setLocalTerminalTick((n) => n + 1);
      return next;
    });
  };

  return (
    <div
      className={`min-w-0 min-h-0 flex overflow-hidden bg-neutral-950 ${
        isMobileSlice ? 'flex-1 w-full border-0' : 'flex-1 border-r border-neutral-800'
      }`}
    >
      {showFiles ? (
        <div
          className={`min-h-0 ${
            isMobileSlice ? 'w-full flex-1' : 'w-[min(42vw,180px)] sm:w-[210px] flex-shrink-0'
          }`}
        >
          <AgentFileTree
            projectId={projectId}
            onSelectProject={(id) => {
              onProjectIdChange?.(id);
              onActiveFilePathChange?.('');
              setCreateHint(false);
            }}
            selectedPath={activeFilePath}
            pinnedPaths={pinnedPaths}
            onPinFile={onPinFile}
            onSelectFile={(path, opts) => {
              onActiveFilePathChange?.(path);
              setCreateHint(!!opts?.create);
              setMainTab('editor');
            }}
            refreshKey={localTreeTick}
          />
        </div>
      ) : null}

      {showEditor ? (
        <div className="flex-1 min-w-0 min-h-0 flex flex-col">
          <div className="flex-shrink-0 flex items-center gap-1 px-2 py-1.5 border-b border-neutral-800 bg-neutral-950 overflow-x-auto">
            {!isMobileSlice ? (
              <span className="sr-only">
                <FolderTree className="w-3 h-3" />
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => setMainTab('editor')}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-medium touch-manipulation ${
                mainTab === 'editor'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300'
              }`}
            >
              <Code2 className="w-3.5 h-3.5" />
              Editor
            </button>
            <button
              type="button"
              onClick={() => {
                setMainTab('live');
                setLocalPreviewTick((n) => n + 1);
              }}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-medium touch-manipulation ${
                mainTab === 'live'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300'
              }`}
            >
              <Globe className="w-3.5 h-3.5" />
              Live
            </button>

            <div className="flex-1" />

            <button
              type="button"
              onClick={toggleTerminal}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[11px] font-medium touch-manipulation ${
                terminalOpen
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'text-neutral-500 hover:bg-neutral-800 hover:text-neutral-300'
              }`}
              title={terminalOpen ? 'Terminal ausblenden' : 'Terminal einblenden'}
              aria-pressed={terminalOpen}
            >
              <Terminal className="w-3.5 h-3.5" />
              Terminal
              {terminalOpen ? (
                <ChevronDown className="w-3 h-3 opacity-70" />
              ) : (
                <ChevronUp className="w-3 h-3 opacity-70" />
              )}
            </button>
          </div>

          {writingElsewhere ? (
            <div className="flex-shrink-0 flex items-center gap-2 px-2.5 py-1.5 border-b border-sky-500/25 bg-sky-500/10 text-[11px] text-sky-200">
              <span className="min-w-0 flex-1 truncate">
                Agent schreibt <span className="font-mono text-sky-100">{writingName}</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  onFollowAgentFile?.();
                  setMainTab('editor');
                }}
                className="flex-shrink-0 rounded-md px-2 py-0.5 bg-sky-500/25 hover:bg-sky-500/40 text-sky-100 font-medium touch-manipulation"
              >
                Anzeigen
              </button>
            </div>
          ) : null}

          <div ref={splitRef} className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-hidden">
              {mainTab === 'editor' ? (
                <AgentEditor
                  projectId={projectId}
                  filePath={activeFilePath}
                  createHint={createHint}
                  pinned={Array.isArray(pinnedPaths) && pinnedPaths.includes(activeFilePath)}
                  onPinFile={onPinFile}
                  onSaved={() => {
                    setCreateHint(false);
                    setLocalTreeTick((n) => n + 1);
                  }}
                />
              ) : (
                <AgentLiveView projectId={projectId} refreshKey={localPreviewTick} />
              )}
            </div>

            {terminalOpen ? (
              <>
                <div
                  role="separator"
                  aria-orientation="horizontal"
                  aria-label="Terminal-Höhe anpassen"
                  aria-valuemin={TERMINAL_HEIGHT_MIN}
                  aria-valuemax={TERMINAL_HEIGHT_MAX}
                  aria-valuenow={Math.round(terminalHeightPct)}
                  title="Ziehen zum Vergrößern oder Verkleinern"
                  className="relative z-10 flex-shrink-0 h-2 -mt-px cursor-row-resize touch-none group"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    resizingTerminalRef.current = true;
                    resizeStartRef.current = { y: e.clientY, pct: terminalHeightPct };
                    document.body.style.cursor = 'row-resize';
                    document.body.style.userSelect = 'none';
                    try {
                      e.currentTarget.setPointerCapture(e.pointerId);
                    } catch (_) {
                      /* ignore */
                    }
                  }}
                >
                  <span className="pointer-events-none absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-neutral-700/80 group-hover:h-0.5 group-hover:bg-emerald-400/80 group-active:bg-emerald-400" />
                </div>
                <div
                  className="flex-shrink-0 min-h-0 border-t border-neutral-800 overflow-hidden"
                  style={{ height: `${terminalHeightPct}%` }}
                >
                  <AgentTerminal
                    projectId={projectId}
                    refreshKey={localTerminalTick}
                    active={terminalOpen}
                    onClose={toggleTerminal}
                  />
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AgentWorkspace;
